import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  Checkbox,
  Container,
  FormControlLabel,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { FormField, FormFieldOption } from '../types';
import TextFieldInput from './PatientInfoForm/TextFieldInput';
import SelectFieldInput from './PatientInfoForm/SelectFieldInput';
import CheckboxFieldInput from './PatientInfoForm/CheckboxFieldInput';
import InfoIcon from '@mui/icons-material/Info';
interface PatientInfoFormProps {
  fields: FormField[];
  lookups: Record<string, FormFieldOption[]>;
  onPatientInfoSubmit: (formData: FormData, offlineQueue: boolean) => void;
  onPatientInfoChange: () => void;
}

export const PatientInfoForm: React.FC<PatientInfoFormProps> = ({
  fields,
  lookups,
  onPatientInfoSubmit,
  onPatientInfoChange,
}) => {
  const [offlineQueue, setOfflineQueue] = useState<boolean>(false);
  const formRef = useRef<HTMLFormElement>(null);
  const phoneInputRef = useRef<HTMLInputElement>(null);

  const getPhoneFieldValue = () => {
    if (!formRef.current) return '';
    const phoneField = fields.find(f => f.type === 'phone');
    if (!phoneField) return '';
    const phoneInput = formRef.current.querySelector(`input[name="${phoneField.name}"]`) as HTMLInputElement;
    return phoneInput?.value || '';
  };

  useEffect(() => {
    if (offlineQueue && phoneInputRef.current) {
      const phoneValue = getPhoneFieldValue();
      if (phoneValue) {
        phoneInputRef.current.value = phoneValue;
      }
    }
    onPatientInfoChange();
  }, [offlineQueue]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    onPatientInfoSubmit(formData, offlineQueue);
  };

  const renderField = (field: FormField, index: number) => {
    if (field.type === 'hidden') return null;

    switch (field.type) {
      case 'lookup': {
        const lookupOptions = lookups[field.lookup || ''] || [];
        return (
          <SelectFieldInput
            key={index}
            field={field}
            options={lookupOptions}
            onChange={(e) => {
              const event = new Event('change', { bubbles: true });
              Object.defineProperty(event, 'target', { value: e.target });
              onPatientInfoChange();
            }}
          />
        );
      }
      case 'dropdown': {
        return (
          <SelectFieldInput
            key={index}
            field={field}
            options={field.options || []}
            onChange={(e) => {
              onPatientInfoChange();
            }}
          />
        );
      }
      case 'checkbox':
        return (
          <CheckboxFieldInput
            key={index}
            field={field}
            onChange={(e) => {
              onPatientInfoChange();
            }}
          />
        );
      default:
        return (
          <TextFieldInput
            key={index}
            field={field}
            onChange={(e) => {
              onPatientInfoChange();
            }}
          />
        );
    }
  };

  if (!fields || fields.length === 0) {
    return null;
  }

  return (
    <Container maxWidth="md">
      <Card
        component="form"
        ref={formRef}
        onSubmit={handleSubmit}
        sx={{
          p: 4,
          my: 2,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
        }}
      >
        <Stack spacing={0}>
          {fields.map((field, index) => renderField(field, index))}
            <FormControlLabel
              label="Enable Offline Queueing"
              control={
                <Checkbox
                  checked={offlineQueue}
                  onChange={() => setOfflineQueue(!offlineQueue)}
                />
              }
            />
            {offlineQueue &&
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                  <Typography
                    component="label"
                    sx={{ fontWeight: 500, color: '#000000' }}
                    display={'flex'}
                    gap={1}
                  >
                    Phone Number to use with Offline Queueing
                    <Tooltip title="Recall SMS will be sent to the phone number below.">
                      <InfoIcon color='success'/>
                    </Tooltip>
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  name="validatedContactNumber"
                  placeholder='Phone Number'
                  inputRef={phoneInputRef}
                  onChange={() => {
                    onPatientInfoChange();
                  }}
                />
              </Box>
            }
        </Stack>

        <Box sx={{ mt: 2 }} />

        <Button
          type="submit"
          variant="contained"
          color="primary"
          sx={{
            mt: 2,
            px: 3,
            py: 1,
          }}
        >
          Create Patient Link
        </Button>
      </Card>
    </Container>
  );
};

export default PatientInfoForm;
